import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalFooter,
  Button,
  Box,
  Flex,
  Text,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  Avatar,
  Divider,
  useToast,
  InputGroup,
  InputRightElement,
} from '@chakra-ui/react';
import { useDropzone } from 'react-dropzone';
import { BiCloudUpload } from 'react-icons/bi';
import AutocompleteField from './AutocompleteField';
import PaymentAutocomplete from './PaymentAutocomplete';
import BankDetailsForm from './BankDetailsForm';
import PaymentInvoicingNotice from './PaymentInvoicingNotice';
import RefundFormLabel from './RefundFormLabel';
import {
  createRefund,
  uploadRefundFile,
  getEligiblePayments,
  searchStudents,
  formatStudentOption,
  formatPaymentOption,
  REASON_OPTIONS,
  formatCOP,
  previewRefundAmount,
  getStudentDisplayName,
  getStudentShortName,
  formatIdentityNumber,
} from 'api/administrador/refunds';

const EMPTY_BANK = {
  bank_holder: '',
  bank_document: '',
  bank_name: '',
  bank_account_type: '',
  bank_account_number: '',
};

function getBankDefaultsFromStudent(student) {
  if (!student) return { ...EMPTY_BANK };
  const shortName = getStudentShortName(student);
  return {
    bank_holder: shortName !== '—' ? shortName : getStudentDisplayName(student),
    bank_document: student.identity ? formatIdentityNumber(student.identity) : '',
    bank_name: '',
    bank_account_type: '',
    bank_account_number: '',
  };
}

function SectionHeader({ number, title }) {
  return (
    <Flex align="center" gap={2} mb={4}>
      <Flex
        align="center"
        justify="center"
        w="28px"
        h="28px"
        borderRadius="full"
        bg="#574feb"
        color="white"
        fontSize="sm"
        fontWeight="bold"
      >
        {number}
      </Flex>
      <Text color="#574feb" fontWeight="bold">
        {title}
      </Text>
    </Flex>
  );
}

export default function RegisterRefundModal({ isOpen, onClose, onSuccess, prefillStudent = null }) {
  const toast = useToast();

  const [studentQuery, setStudentQuery] = useState('');
  const [studentOptions, setStudentOptions] = useState([]);
  const [searchingStudents, setSearchingStudents] = useState(false);
  const [student, setStudent] = useState(null);

  const [payments, setPayments] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [paymentQuery, setPaymentQuery] = useState('');
  const [selectedPaymentId, setSelectedPaymentId] = useState('');

  const [gatewayDiscountPercent, setGatewayDiscountPercent] = useState('8');
  const [reason, setReason] = useState('');
  const [reasonDetail, setReasonDetail] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [bankDetails, setBankDetails] = useState(EMPTY_BANK);
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const selectedPayment = payments.find((p) => String(p.id) === String(selectedPaymentId));
  const discountAmount = selectedPayment
    ? Math.round(selectedPayment.amount * (Number(gatewayDiscountPercent) / 100))
    : 0;
  const previewAmount = selectedPayment
    ? previewRefundAmount(selectedPayment.amount, gatewayDiscountPercent)
    : null;

  const resetForm = useCallback(() => {
    setStudentQuery('');
    setStudentOptions([]);
    setStudent(null);
    setPayments([]);
    setPaymentQuery('');
    setSelectedPaymentId('');
    setGatewayDiscountPercent('8');
    setReason('');
    setReasonDetail('');
    setInternalNotes('');
    setBankDetails(EMPTY_BANK);
    setFile(null);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      resetForm();
      return;
    }

    if (prefillStudent) {
      setStudent(prefillStudent);
      setStudentQuery(formatStudentOption(prefillStudent));
      setBankDetails(getBankDefaultsFromStudent(prefillStudent));
    }
  }, [isOpen, prefillStudent, resetForm]);

  useEffect(() => {
    if (!student?.id) {
      setPayments([]);
      setPaymentQuery('');
      setSelectedPaymentId('');
      return;
    }

    setLoadingPayments(true);
    getEligiblePayments(student.id)
      .then((data) => {
        setPayments(data);
        if (data.length === 1) {
          setSelectedPaymentId(String(data[0].id));
          setPaymentQuery(formatPaymentOption(data[0]));
        }
      })
      .catch(() => {
        toast({ title: 'Error al cargar pagos elegibles', status: 'error', duration: 4000 });
      })
      .finally(() => setLoadingPayments(false));
  }, [student, toast]);

  const handleStudentSearch = useCallback(async (term) => {
    try {
      setSearchingStudents(true);
      const results = await searchStudents(term);
      setStudentOptions(results);
    } catch {
      setStudentOptions([]);
    } finally {
      setSearchingStudents(false);
    }
  }, []);

  const handleStudentSelect = (selected) => {
    setStudent(selected);
    setStudentQuery(formatStudentOption(selected));
    setStudentOptions([]);
    setPaymentQuery('');
    setSelectedPaymentId('');
    setBankDetails(getBankDefaultsFromStudent(selected));
  };

  const handleStudentInputChange = (value) => {
    setStudentQuery(value);
    if (student && value !== formatStudentOption(student)) {
      setStudent(null);
      setPayments([]);
      setPaymentQuery('');
      setSelectedPaymentId('');
      setBankDetails(EMPTY_BANK);
    }
  };

  const handleBankDetailsChange = (partial) => {
    setBankDetails((prev) => ({ ...prev, ...partial }));
  };

  const handlePaymentSelect = (payment) => {
    setSelectedPaymentId(String(payment.id));
    setPaymentQuery(formatPaymentOption(payment));
  };

  const onDrop = useCallback(
    (acceptedFiles) => {
      const f = acceptedFiles[0];
      if (f && f.size > 5 * 1024 * 1024) {
        toast({ title: 'El archivo no puede superar 5 MB', status: 'warning', duration: 4000 });
        return;
      }
      setFile(f || null);
    },
    [toast]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'] },
    maxFiles: 1,
  });

  const handleSubmit = async () => {
    const hasBankDetails = Object.values(bankDetails).every((value) => String(value).trim());

    if (!student || !selectedPaymentId || !reason || !file || !hasBankDetails) {
      toast({ title: 'Completa todos los campos obligatorios', status: 'warning', duration: 4000 });
      return;
    }

    setSubmitting(true);
    try {
      const response = await createRefund({
        student: student.id,
        payment: Number(selectedPaymentId),
        reason,
        reason_detail: reasonDetail || null,
        internal_notes: internalNotes || null,
        gateway_discount_percent: Number(gatewayDiscountPercent),
        ...bankDetails,
      });

      const newId = response?.id || response?.data?.id;
      if (newId && file) {
        await uploadRefundFile({ file, refId: newId, field: 'signed_letter' });
      }

      toast({ title: 'Devolución registrada', status: 'success', duration: 4000 });
      onSuccess?.();
    } catch (error) {
      const message =
        error?.response?.data?.error?.message || error?.response?.data?.message || 'Error al registrar devolución';
      toast({ title: message, status: 'error', duration: 5000 });
    } finally {
      setSubmitting(false);
    }
  };

  const studentMeta = student
    ? [
        `Código ${student.id}`,
        student.identity ? `${student.identity_type?.short || 'CC'} ${student.identity}` : null,
        student.program_type?.name || student.enrollments?.[0]?.program_type?.name,
        student.status || 'Activo',
      ]
        .filter(Boolean)
        .join(' · ')
    : '';

  return (
    <Modal isOpen={isOpen} onClose={onClose} scrollBehavior="outside" size="xl">
      <ModalOverlay />
      <ModalContent maxW="840px" mx={4}>
        <Box bg="#574feb" color="white" px={6} py={4} borderTopRadius="md">
          <Text fontSize="lg" fontWeight="bold">
            Registrar devolución
          </Text>
        </Box>

        <ModalBody py={6}>
          <SectionHeader number={1} title="Estudiante y pago" />

          {!prefillStudent && (
            <Box mb={4}>
              <AutocompleteField
                label="Buscar estudiante"
                isRequired
                placeholder="Código, nombre o correo del estudiante"
                value={studentQuery}
                onChange={handleStudentInputChange}
                onSearch={handleStudentSearch}
                options={studentOptions}
                onSelect={handleStudentSelect}
                getOptionLabel={formatStudentOption}
                getOptionKey={(s) => s.id}
                isLoading={searchingStudents}
                emptyMessage="No se encontraron estudiantes"
                minLength={2}
                debounceMs={400}
              />
            </Box>
          )}

          {student && (
            <Box bg="#574feb10" borderRadius="md" p={4} mb={4}>
              <Flex align="center" gap={3}>
                <Avatar name={getStudentDisplayName(student)} bg="#574feb" color="white" size="md" />
                <Box>
                  <Text fontWeight="bold">{getStudentDisplayName(student)}</Text>
                  <Text fontSize="sm" color="gray.600">
                    {studentMeta}
                  </Text>
                </Box>
              </Flex>
            </Box>
          )}

          {student && (
            <>
              <PaymentAutocomplete
                payments={payments}
                value={selectedPaymentId}
                inputValue={paymentQuery}
                onInputChange={(value) => {
                  setPaymentQuery(value);
                  if (selectedPaymentId) {
                    const selected = payments.find((p) => String(p.id) === String(selectedPaymentId));
                    if (selected && value !== formatPaymentOption(selected)) {
                      setSelectedPaymentId('');
                    }
                  }
                }}
                onSelect={handlePaymentSelect}
                isDisabled={!student}
                isLoading={loadingPayments}
              />

              {selectedPayment && <PaymentInvoicingNotice payment={selectedPayment} />}

              <FormControl mt={4}>
                <RefundFormLabel required>Descuento de pasarela (%)</RefundFormLabel>
                <InputGroup maxW="140px">
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    step="0.01"
                    value={gatewayDiscountPercent}
                    onChange={(e) => setGatewayDiscountPercent(e.target.value)}
                  />
                  <InputRightElement pointerEvents="none" color="gray.500" fontSize="sm">
                    %
                  </InputRightElement>
                </InputGroup>
                <Text fontSize="xs" color="gray.500" mt={2} maxW="520px">
                  Porcentaje de descuento a aplicar. Editable según el caso. El resumen se recalcula automáticamente.
                </Text>
              </FormControl>

              {selectedPayment && (
                <Box mt={4} p={4} border="1px solid #9e46d8" borderRadius="md" bg="white">
                  <Flex justify="space-between" mb={2}>
                    <Text fontSize="sm" color="gray.600">
                      Valor pagado:
                    </Text>
                    <Text fontSize="sm">{formatCOP(selectedPayment.amount)} COP</Text>
                  </Flex>
                  <Flex justify="space-between" mb={2}>
                    <Text fontSize="sm" color="red.500">
                      Descuento ({gatewayDiscountPercent}%):
                    </Text>
                    <Text fontSize="sm" color="red.500">
                      -{formatCOP(discountAmount)} COP
                    </Text>
                  </Flex>
                  <Divider borderColor="#574feb" borderWidth="1px" my={3} />
                  <Flex justify="space-between">
                    <Text fontWeight="bold" color="#574feb">
                      Valor a devolver:
                    </Text>
                    <Text fontWeight="bold" fontSize="lg">
                      {formatCOP(previewAmount)} COP
                    </Text>
                  </Flex>
                </Box>
              )}
            </>
          )}

          {student && (
            <Box mt={6}>
              <SectionHeader number={2} title="Datos bancarios destino" />
              <BankDetailsForm values={bankDetails} onChange={handleBankDetailsChange} />
            </Box>
          )}

          <Box mt={6}>
            <SectionHeader number={3} title="Motivo de la devolución" />
            <FormControl isRequired mb={3}>
              <RefundFormLabel required>Motivo</RefundFormLabel>
              <Select placeholder="Selecciona un motivo" value={reason} onChange={(e) => setReason(e.target.value)}>
                {REASON_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Detalle del motivo (opcional)</FormLabel>
              <Textarea
                placeholder="Contexto adicional sobre la solicitud de devolución..."
                value={reasonDetail}
                onChange={(e) => setReasonDetail(e.target.value)}
                rows={3}
              />
            </FormControl>
          </Box>

          <Box mt={6}>
            <SectionHeader number={4} title="Carta firmada" />
            <FormControl isRequired mb={4}>
              <RefundFormLabel required>Carta firmada (PDF)</RefundFormLabel>
              <Box
                {...getRootProps()}
                border="2px dashed"
                borderColor={isDragActive ? '#574feb' : 'gray.300'}
                borderRadius="md"
                p={6}
                textAlign="center"
                cursor="pointer"
                bg={isDragActive ? '#574feb08' : 'white'}
              >
                <input {...getInputProps()} />
                <BiCloudUpload size={28} color="#574feb" style={{ margin: '0 auto 8px' }} />
                <Text color="#574feb" fontWeight="semibold">
                  Selecciona archivo o arrastra aquí
                </Text>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Solo PDF · máx 5 MB
                </Text>
              </Box>
              {file && (
                <Flex mt={2} align="center" gap={2}>
                  <Text fontSize="sm" color="green.600">
                    ✓ {file.name}
                  </Text>
                  <Button size="xs" variant="ghost" colorScheme="red" onClick={() => setFile(null)}>
                    X
                  </Button>
                </Flex>
              )}
            </FormControl>

            <SectionHeader number={5} title="Notas internas (opcional)" />
            <Textarea
              placeholder="Notas para el equipo interno (no visibles para el estudiante)..."
              value={internalNotes}
              onChange={(e) => setInternalNotes(e.target.value)}
              rows={3}
            />
          </Box>
        </ModalBody>

        <ModalFooter borderTop="1px solid" borderColor="gray.200" gap={2}>
          <Button variant="outline" onClick={onClose} isDisabled={submitting}>
            Cancelar
          </Button>
          <Button bg="#574feb" color="white" _hover={{ bg: '#4840d4' }} onClick={handleSubmit} isLoading={submitting}>
            Registrar devolución
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
